import { useState, useEffect } from 'react';
import { getUserData } from '~/api/user';
import { getCookie } from '~/services/cookies.client';
import type { UserData } from '~/types/userData';
import InformationalBanner from '../InformationalBanner';

import { PhoneVerificationModal } from './PhoneVerificationModal';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import TextButton from '../buttons/TextButton';

declare let bootstrap: any;

const PhoneVerificationBanner = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<UserData | null>(null);
  const [OTPBannerClosedAtValue, setOTPBannerClosedAtValue] = useState<string | null>(null);

  const openModal = () => {
    const modal = bootstrap.Modal.getOrCreateInstance('#phoneVerificationModal');
    modal?.show();
  };

  const closeBanner = () => {
    sessionStorage.setItem('OTPBannerDisabledAt', DateTime.utc().toISO() || '');
    setOTPBannerClosedAtValue(DateTime.utc().toISO());
    const modal = bootstrap.Modal.getOrCreateInstance('#phoneVerificationModal');
    modal?.hide();
  };

  useEffect(() => {
    const OTPBannerDisabledAtValue = sessionStorage.getItem('OTPBannerDisabledAt');
    setOTPBannerClosedAtValue(OTPBannerDisabledAtValue);
    const userData = async () => {
      const token = getCookie('FO-JWT');
      const { user } = await getUserData(token);
      setUser(user);
    };
    userData();
  }, []);

  const OTPBannerClosedInLast12Hours =
    OTPBannerClosedAtValue && DateTime.utc().diff(DateTime.fromISO(OTPBannerClosedAtValue), 'hours').hours < 12;

  const bannerText = () => {
    if (!user) return '';
    if (user.isHost) {
      return t('otp.banner.host.text');
    }
    if (user.requestPresent) {
      return t('otp.banner.guest.withCR');
    }
    return t('otp.banner.guest.noCR');
  };

  //Banner is not shown if:
  //User is not logged in
  //User has already verified their phone
  //A guest without requests closed the banner less than 12 hours ago
  if (
    !user ||
    (user && user.phoneVerified) ||
    (user && !user.isHost && !user.requestPresent && OTPBannerClosedInLast12Hours)
  ) {
    return null;
  }

  const cta = (
    <TextButton
      variant="tertiaryWhite"
      size="sm"
      text={t(`otp.banner.cta.${user.phoneComplete ? 'verify' : 'addPhoneNumber'}`)}
      onClick={() => {
        openModal();
      }}
      icon="long-arrow-right"
    />
  );

  return (
    <>
      <InformationalBanner
        variant="accent"
        cta={cta}
        extraClasses="phone-verification-banner"
        icon="shield-outline"
        text={bannerText()}
        showCloseButton={!user.requestPresent && !user.isHost}
        onClose={closeBanner}
      />
      <PhoneVerificationModal
        initialUserData={user}
        onPhoneNumberVerification={(user) => setUser(user)}
      />
    </>
  );
};

export default PhoneVerificationBanner;
